import { useEffect, useState, useMemo } from "react";
import Lottie from "lottie-react";
import * as animationData from "../../assets/lotties/countdown.json";
import spinnerGif from "../../assets/feedback-loading-spinner.gif";

interface PraxisLoadingProps {
  title?: string;
  countdownFrom?: number;
  onCountdownComplete?: () => void;
  showCountdown?: boolean;
}

export function PraxisLoading({ 
  title = "PraxisPlay", 
  countdownFrom = 8,
  onCountdownComplete,
  showCountdown = true,
}: PraxisLoadingProps) {
  const [countdown, setCountdown] = useState(countdownFrom);
  
  // Filter out text layers (ty: 5) which contain the countdown numbers
  const filteredAnimationData = useMemo(() => {
    const data = JSON.parse(JSON.stringify(animationData));
    if (data.layers) {
      data.layers = data.layers.filter((layer: any) => layer.ty !== 5);
    }
    return data;
  }, []);

  useEffect(() => {
    if (!showCountdown) {
      return;
    }

    if (countdown <= 0) {
      onCountdownComplete?.();
      return;
    }

    const duration = 1000; // 1000 ms per countdown number

    // Decrement countdown after 1 second
    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [countdown, onCountdownComplete, showCountdown]);
  

  /*
   * Match the live call screen's ai-interaction-container exactly (no extra padding,
   * a single flex-col child) so the hero element (spinner/countdown) is centered on
   * the same point as the logo underneath, and titles are absolutely positioned so
   * they don't shift that centering.
   */
  return (
    <div className="fixed z-[4] bg-callLoadingBg top-0 bottom-0 left-0 right-0 flex flex-col items-center justify-center">
      <h1 className="absolute top-[20%] left-0 right-0 text-center text-[42px] text-text-secondary font-bold z-10">
        {showCountdown ? "Session begins in" : title}
      </h1>
      {showCountdown ? (
        <div className="ai-interaction-container w-full">
          <div className="flex flex-col items-center gap-6">
            {/* Countdown in first slot so it lines up with logo behind overlay */}
            <div className="relative flex items-center justify-center w-[300px] h-[300px]">
              <div className="praxis-loading-countdown-lottie">
                <Lottie
                  animationData={filteredAnimationData}
                  loop={true}
                  className="w-[300px] h-[300px]"
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <span className="text-[72px] text-text-secondary font-bold">
                  {countdown > 0 ? countdown : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Loading state (e.g. before 3-2-1): same layout as call screen so spinner lines up with logo */
        <div className="ai-interaction-container w-full">
          <div className="flex flex-col items-center gap-6">
            <img src={spinnerGif} alt="" className="w-[249px] h-[249px]" />
          </div>
        </div>
      )}

      <div className="absolute bottom-8 left-0 right-0 max-w-[460px] mx-auto flex px-4">
        <span className="text-[22px] text-center font-light text-text-secondary">
          This session is being recorded and is intended for training purposes.
        </span>
      </div>
    </div>
  );
}
